import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLResponsiveImages = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Responsive Images (srcset, picture)</h1>
                <p className="hero-lead">
                    Serve the perfect image for every device and screen resolution. Master srcset, sizes,
                    and the picture element for optimal performance and visual quality.
                </p>
            </div>

            <div className="content-section">
                <h2>The Responsive Image Problem</h2>
                <p>
                    Traditional <code>&lt;img src="..."&gt;</code> serves the same image to all devices -
                    wasting bandwidth on mobile and delivering low-quality images on high-DPI displays.
                </p>

                <div className="problem-stats">
                    <div className="stat-card">
                        <h3>4x</h3>
                        <p>data wasted serving desktop images to mobile</p>
                    </div>
                    <div className="stat-card">
                        <h3>2-3x</h3>
                        <p>pixel density on modern screens (Retina/HDPI)</p>
                    </div>
                    <div className="stat-card">
                        <h3>60%</h3>
                        <p>of web traffic is mobile (2024)</p>
                    </div>
                </div>

                <div className="comparison-example">
                    <div className="comparison-card error">
                        <h3> Problem: One Size For All</h3>
                        <CodeBlock language="html" code={`<img src="image-4000px.jpg" alt="Photo">

<!-- Issues: --&gt;
<!-- - Mobile downloads 4MB file for 400px display --&gt;
<!-- - High-DPI screens show blurry image --&gt;
<!-- - Waste of bandwidth and loading time --&gt;`} />
                    </div>

                    <div className="comparison-card">
                        <h3>Solution: Responsive Images</h3>
                        <CodeBlock language="html" code={`<img src="image-400.jpg"
     srcset="image-400.jpg 400w,
             image-800.jpg 800w,
             image-1600.jpg 1600w"
     sizes="(max-width: 600px) 400px, 800px"
     alt="Photo">

<!-- Browser downloads the right size! --&gt;`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The srcset Attribute</h2>
                <p>
                    <code>srcset</code> provides multiple image sources. Browser chooses based on device capabilities.
                </p>

                <h3>1. Resolution Switching (w descriptor)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="photo-400.jpg"
     srcset="photo-400.jpg 400w,
             photo-800.jpg 800w,
             photo-1200.jpg 1200w,
             photo-1600.jpg 1600w"
     sizes="100vw"
     alt="Landscape photo"
     width="1600"
     height="900">

<!-- How it works: --&gt;
<!-- - w = actual width of image file in pixels --&gt;
<!-- - 400w means image is 400 pixels wide --&gt;
<!-- - Browser picks based on screen size & DPI --&gt;`} />
                </div>

                <h3>Understanding 'w' Descriptor:</h3>
                <ul>
                    <li><code>400w</code> = "This image file is 400 pixels wide"</li>
                    <li>NOT a media query</li>
                    <li>Browser calculates: needed_width / dpr</li>
                    <li>Then selects closest match from srcset</li>
                </ul>

                <div className="example-box">
                    <h4>Real-World Example:</h4>
                    <p><strong>Device:</strong> iPhone with 375px viewport, 3x DPR</p>
                    <p><strong>Calculation:</strong> 375px  3 = 1125px needed</p>
                    <p><strong>Browser selects:</strong> photo-1200.jpg (closest larger)</p>
                </div>

                <h3>2. Pixel Density Switching (x descriptor)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="icon.png"
     srcset="icon-1x.png 1x,
             icon-2x.png 2x,
             icon-3x.png 3x"
     alt="Icon"
     width="100"
     height="100">

<!-- Use for: --&gt;
<!-- - Fixed-size images (icons, logos) --&gt;
<!-- - When you know exact display size --&gt;

<!-- Examples: --&gt;
<!-- 1x = Standard displays (72-96 DPI) --&gt;
<!-- 2x = Retina/HD displays (144-192 DPI) --&gt;
<!-- 3x = Super Retina (216+ DPI) --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The sizes Attribute</h2>
                <p>
                    <code>sizes</code> tells the browser how much space the image will take in the layout.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic sizes Syntax</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="photo.jpg"
     srcset="photo-400.jpg 400w,
             photo-800.jpg 800w,
             photo-1200.jpg 1200w"
     sizes="(max-width: 600px) 400px,
            (max-width: 1000px) 800px,
            1200px"
     alt="Photo">

<!-- Reads as: --&gt;
<!-- "If viewport  600px, image will be 400px wide" --&gt;
<!-- "If viewport  1000px, image will be 800px wide" --&gt;
<!-- "Otherwise, image will be 1200px wide" --&gt;`} />
                </div>

                <h3>Common sizes Patterns:</h3>

                <div className="code-example">
                    <div className="code-header">
                        <span>Full Width Image</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="hero.jpg"
     srcset="hero-800.jpg 800w, hero-1600.jpg 1600w"
     sizes="100vw"
     alt="Hero image">`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Half Width on Desktop</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="photo.jpg"
     srcset="photo-400.jpg 400w, photo-800.jpg 800w"
     sizes="(min-width: 800px) 50vw, 100vw"
     alt="Photo">`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Grid Layout</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="thumbnail.jpg"
     srcset="thumb-200.jpg 200w, thumb-400.jpg 400w"
     sizes="(min-width: 1200px) 25vw,
            (min-width: 800px) 33vw,
            (min-width: 500px) 50vw,
            100vw"
     alt="Thumbnail">

<!-- 4 columns on large screens (25%) --&gt;
<!-- 3 columns on medium screens (33%) --&gt;
<!-- 2 columns on small screens (50%) --&gt;
<!-- 1 column on mobile (100%) --&gt;`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Max Width Container</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="content.jpg"
     srcset="content-600.jpg 600w, content-800.jpg 800w"
     sizes="(min-width: 1000px) 800px, 90vw"
     alt="Content image">

<!-- Fixed 800px on wide screens --&gt;
<!-- 90% of viewport on smaller screens --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;picture&gt; Element</h2>
                <p>
                    <code>&lt;picture&gt;</code> provides full control: different images for different scenarios.
                </p>

                <h3>1. Art Direction</h3>
                <p>Different crops/compositions for different screen sizes:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Wide landscape for desktop --&gt;
    <source media="(min-width: 1000px)" 
            srcset="hero-wide.jpg">
    
    <!-- Square crop for tablets --&gt;
    <source media="(min-width: 600px)" 
            srcset="hero-square.jpg">
    
    <!-- Vertical crop for mobile (portrait-focused) --&gt;
    <img src="hero-vertical.jpg" 
         alt="Hero image">
</picture>

<!-- Use when: --&gt;
<!-- - Focal point changes (portrait vs landscape) --&gt;
<!-- - Different aspect ratios needed --&gt;
<!-- - Important content must stay visible --&gt;`} />
                </div>

                <h3>2. Format Switching</h3>
                <p>Serve modern formats with fallbacks:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Try AVIF first (best compression) --&gt;
    <source type="image/avif" 
            srcset="photo.avif">
    
    <!-- Try WebP (good compression) --&gt;
    <source type="image/webp" 
            srcset="photo.webp">
    
    <!-- Fallback to JPEG (universal) --&gt;
    <img src="photo.jpg" 
         alt="Photo">
</picture>`} />
                </div>

                <h3>3. Combining Everything</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Format + Resolution + Art Direction</span>
                    </div>
                    <CodeBlock language="html" code={`<picture>
    <!-- Desktop: AVIF, multiple resolutions --&gt;
    <source media="(min-width: 1000px)"
            type="image/avif"
            srcset="desktop-800.avif 800w,
                    desktop-1600.avif 1600w"
            sizes="1000px">
    
    <!-- Desktop: WebP fallback --&gt;
    <source media="(min-width: 1000px)"
            type="image/webp"
            srcset="desktop-800.webp 800w,
                    desktop-1600.webp 1600w"
            sizes="1000px">
    
    <!-- Mobile: AVIF, smaller images --&gt;
    <source type="image/avif"
            srcset="mobile-400.avif 400w,
                    mobile-800.avif 800w"
            sizes="100vw">
    
    <!-- Mobile: WebP fallback --&gt;
    <source type="image/webp"
            srcset="mobile-400.webp 400w,
                    mobile-800.webp 800w"
            sizes="100vw">
    
    <!-- Universal fallback --&gt;
    <img src="mobile-400.jpg"
         srcset="mobile-400.jpg 400w,
                 mobile-800.jpg 800w"
         sizes="100vw"
         alt="Photo"
         width="800"
         height="600"
         loading="lazy">
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Loading Strategies</h2>

                <h3>1. Lazy Loading</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Lazy load below-the-fold images --&gt;
<img src="photo.jpg"
     srcset="photo-400.jpg 400w, photo-800.jpg 800w"
     sizes="100vw"
     loading="lazy"
     alt="Photo">

<!-- loading attribute: --&gt;
<!-- - lazy: Load when near viewport (default for most browsers) --&gt;
<!-- - eager: Load immediately --&gt;
<!-- - auto: Browser decides --&gt;`} />
                </div>

                <h3>2. Preload Critical Images</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<head>
    <!-- Preload hero image for instant display --&gt;
    <link rel="preload" 
          as="image" 
          href="hero.jpg"
          imagesrcset="hero-800.jpg 800w, hero-1600.jpg 1600w"
          imagesizes="100vw">
</head>

<body>
    <img src="hero.jpg"
         srcset="hero-800.jpg 800w, hero-1600.jpg 1600w"
         sizes="100vw"
         alt="Hero"
         loading="eager">
</body>`} />
                </div>

                <h3>3. Decoding Strategy</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Async decoding (don't block rendering) --&gt;
<img src="large-photo.jpg" 
     decoding="async" 
     alt="Photo">

<!-- sync: Block rendering until decoded --&gt;
<!-- async: Decode asynchronously (recommended for large images) --&gt;
<!-- auto: Browser decides --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Examples</h2>

                <h3>Blog Post Featured Image</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <source type="image/webp"
            srcset="featured-400.webp 400w,
                    featured-800.webp 800w,
                    featured-1200.webp 1200w"
            sizes="(min-width: 1000px) 800px, 90vw">
    
    <img src="featured-800.jpg"
         srcset="featured-400.jpg 400w,
                 featured-800.jpg 800w,
                 featured-1200.jpg 1200w"
         sizes="(min-width: 1000px) 800px, 90vw"
         alt="Article featured image"
         width="1200"
         height="675"
         loading="lazy">
</picture>`} />
                </div>

                <h3>Product Thumbnail Grid</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="product-thumb.jpg"
     srcset="product-thumb-200.jpg 200w,
             product-thumb-400.jpg 400w"
     sizes="(min-width: 1200px) 20vw,
            (min-width: 800px) 25vw,
            (min-width: 500px) 33vw,
            50vw"
     alt="Product name"
     width="400"
     height="400"
     loading="lazy">`} />
                </div>

                <h3>Hero Banner with Art Direction</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<picture>
    <!-- Desktop: wide 21:9 crop --&gt;
    <source media="(min-width: 1200px)"
            type="image/webp"
            srcset="hero-wide-1920.webp 1920w,
                    hero-wide-2560.webp 2560w"
            sizes="100vw">
    
    <!-- Tablet: 16:9 crop --&gt;
    <source media="(min-width: 768px)"
            type="image/webp"
            srcset="hero-tablet-1024.webp 1024w,
                    hero-tablet-1536.webp 1536w"
            sizes="100vw">
    
    <!-- Mobile: 4:5 portrait crop --&gt;
    <img src="hero-mobile-750.jpg"
         srcset="hero-mobile-750.jpg 750w,
                 hero-mobile-1125.jpg 1125w"
         sizes="100vw"
         alt="Hero image"
         width="750"
         height="937"
         loading="eager">
</picture>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-list">
                    <h3>Do:</h3>
                    <ul>
                        <li>Always specify <code>width</code> and <code>height</code> (prevents layout shift)</li>
                        <li>Use <code>loading="lazy"</code> for below-the-fold images</li>
                        <li>Provide at least 3-4 size variants (small, medium, large, xlarge)</li>
                        <li>Use WebP/AVIF with JPEG/PNG fallback</li>
                        <li>Set <code>sizes</code> to match your actual layout</li>
                        <li>Compress all images before upload</li>
                        <li>Use CDN for automatic image optimization</li>
                        <li>Test on real devices with throttled connection</li>
                    </ul>

                    <h3> Don't:</h3>
                    <ul>
                        <li>Forget the fallback <code>src</code> attribute</li>
                        <li>Use srcset without sizes (except for x descriptor)</li>
                        <li>Provide only 1-2 image sizes (too limited)</li>
                        <li>Set sizes to incorrect values</li>
                        <li>Lazy load above-the-fold images</li>
                        <li>Skip width/height attributes</li>
                        <li>Serve unoptimized original images</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Testing & Debugging</h2>
                <ul>
                    <li><strong>Chrome DevTools:</strong> Network tab shows which image was downloaded</li>
                    <li><strong>Responsive Mode:</strong> Test different viewports and DPR</li>
                    <li><strong>Lighthouse:</strong> Audits image sizing and format</li>
                    <li><strong>Real Devices:</strong> Test on actual mobile/tablet</li>
                    <li><strong>Network Throttling:</strong> Simulate slow connections</li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>Debug in Browser Console</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Check which image was loaded
const img = document.querySelector('img');
console.log('Current src:', img.currentSrc);
console.log('Natural size:', img.naturalWidth, 'x', img.naturalHeight);

// Check device pixel ratio
console.log('Device pixel ratio:', window.devicePixelRatio);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Automation Tools</h2>
                <ul>
                    <li><strong>Cloudinary:</strong> Automatic responsive images via URL parameters</li>
                    <li><strong>imgix:</strong> Real-time image processing and optimization</li>
                    <li><strong>Sharp (Node.js):</strong> Generate multiple sizes programmatically</li>
                    <li><strong>Responsive Images Breakpoints Generator:</strong> Calculate optimal breakpoints</li>
                    <li><strong>Webpack/Vite plugins:</strong> Automatic image optimization in build</li>
                </ul>
            </div>

        </div>
    );
};

export default HTMLResponsiveImages;




